import {
    FC,
    ReactNode,
    createContext,
    useContext,
} from "react";
import { useUserContext } from "./userProvider";
import { AccountType, Id, Review, RolesForEvent, ReSession } from "../interfaces/types";

// Define the type for the context, including optional events array and functions to add and update events
export type PermissionContextType = {
    hasEventEditPermission: (eventId: Id) => boolean; 
    hasEventManagerSelectionPermission: (eventId: Id) => boolean;
    
    hasSessionSubmitPermission: () => boolean; 

    hasSessionOverviewViewPermission: (eventId: Id) => boolean;
    hasSessionOverviewEditPermission: (eventId: Id) => boolean;
    hasSessionEditPermission: (eventId: Id, session: ReSession) => boolean;

    hasReviewAndCommentViewPermission: (eventId: Id) => boolean;
    hasReviewCreatePermission: (eventId: Id) => boolean;
    hasReviewEditPermission: (eventId: Id, review: Review) => boolean;
    hasCommentCreatePermission: (eventId: Id) => boolean;

    hasManagerOrHigherPermission: (eventId: Id) => boolean;
    hasPlannerEditPermission: (eventId: Id) => boolean;
};

// Create the context with default values
const PermissionContext = createContext<PermissionContextType>({
    hasEventEditPermission: (eventId: Id) => false,
    hasEventManagerSelectionPermission: (eventId: Id) => false,

    hasSessionSubmitPermission: () => false,

    hasSessionOverviewViewPermission: (eventId: Id) => false,
    hasSessionOverviewEditPermission: (eventId: Id) => false,
    hasSessionEditPermission: (eventId: Id, session: ReSession) => false,

    hasReviewAndCommentViewPermission: (eventId: Id) => false,
    hasReviewCreatePermission: (eventId: Id) => false,
    hasReviewEditPermission: (eventId: Id, review: Review) => false,
    hasCommentCreatePermission: (eventId: Id) => false,
    hasManagerOrHigherPermission: (eventId: Id) => false,

    hasPlannerEditPermission: (eventId: Id) => false,
});

// Define the PermissionProvider component which will wrap the children components and provide the context
export const PermissionProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { userDetails } = useUserContext();

    function isUserDetailsDefined(): boolean {
        return !!userDetails;
    }

    function checkAccountType(accountTypes: AccountType[]): boolean {
        return !!userDetails?.accountType && accountTypes.includes(userDetails.accountType);
    }

    function checkEventRoles(eventId: Id | undefined, roles: RolesForEvent[]): boolean {
        if (!eventId) return false;
        const userPermissionsOnEvent = userDetails?.eventRoles.find(eventRole => eventRole.eventId === eventId);
        return userPermissionsOnEvent?.roles.some(role => roles.includes(role)) ?? false;
    }

    function hasEventEditPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    function hasEventManagerSelectionPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN]);
    }

    function hasSessionSubmitPermission(): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return true;
    }

    function hasSessionOverviewViewPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER, RolesForEvent.SUBMITTER, RolesForEvent.REVIEWER]);
    }

    function hasSessionOverviewEditPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return  checkAccountType([AccountType.ADMIN])
             || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    function hasReviewAndCommentViewPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER, RolesForEvent.SUBMITTER, RolesForEvent.PRESENTER]);
    }

    function hasReviewCreatePermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER, RolesForEvent.SUBMITTER, RolesForEvent.PRESENTER]);
    }

    function hasCommentCreatePermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER, RolesForEvent.SUBMITTER, RolesForEvent.PRESENTER]);
    }

    function hasReviewEditPermission(eventId: Id, review: Review){
        if (!isUserDetailsDefined()) { return false; }
        if(review.createdBy === userDetails?.uid) return true;
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    function hasSessionEditPermission(eventId: Id, session: ReSession){
        if (!isUserDetailsDefined()) { return false; }
        if(userDetails && (session.createdBy === userDetails?.uid || session.presenterIds.includes(userDetails?.uid))) return true;
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    function hasManagerOrHigherPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    function hasPlannerEditPermission(eventId: Id): boolean {
        if (!isUserDetailsDefined()) { return false; }
        return checkAccountType([AccountType.ADMIN])
            || checkEventRoles(eventId, [RolesForEvent.MANAGER]);
    }

    const value: PermissionContextType = {
        hasEventEditPermission,
        hasEventManagerSelectionPermission,

        hasSessionSubmitPermission,
        hasSessionOverviewEditPermission,
        hasSessionOverviewViewPermission,
        hasSessionEditPermission,

        hasReviewAndCommentViewPermission,
        hasReviewCreatePermission,
        hasReviewEditPermission,
        hasCommentCreatePermission,

        hasManagerOrHigherPermission,
        hasPlannerEditPermission,
    };

    return (
        // Provide the context to the children components
        <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
    );
};

// Custom hook to use the PermissionContext
export const usePermissionContext = (): PermissionContextType => {
    return useContext(PermissionContext);
};
